import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Capa React de `@continnua/auth-web` (docs/FASE-7.md §6.1). Es deliberadamente delgada: todo lo que
 * decide algo vive en `../client.ts`, que no depende de React y se prueba sin navegador. Aquí solo
 * hay suscripción al estado y dos componentes.
 *
 * React es `peerDependency` opcional, igual que `express` y `@nestjs/common` en `auth-server`: un
 * módulo que use otra biblioteca importa `@continnua/auth-web` a secas y no paga nada por esto.
 *
 * **Sin tema.** Los estilos salen de variables CSS con valor de reserva (`var(--cnt-fg, #111827)`):
 * un módulo que no defina nada obtiene algo legible, y uno que defina sus tokens obtiene su marca.
 * Los tokens de diseño compartidos no están publicados (§3.7) y este paquete no puede depender de
 * ellos.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from "react";
import { createAuthClient } from "../client.js";
import { readOrgFromUrl, withOrgParam } from "../org-link.js";
const ESTADO_INICIAL = { status: "loading", claims: null, orgId: null, orgSlug: null, error: null };
const AuthContext = createContext(null);
export function AuthProvider({ children, orgHint, ...options }) {
    const [state, setState] = useState(ESTADO_INICIAL);
    // El cliente se crea una sola vez: recrearlo en cada render perdería los tokens, que viven en su
    // memoria y en ningún otro sitio.
    const clientRef = useRef(null);
    if (clientRef.current === null) {
        const hint = orgHint ?? readOrgFromUrl(options.currentUrl?.() ?? globalThis.location?.href ?? "");
        clientRef.current = createAuthClient({ ...options, orgHint: hint });
    }
    const client = clientRef.current;
    useEffect(() => {
        const unsubscribe = client.subscribe(setState);
        void client.bootstrap();
        return unsubscribe;
    }, [client]);
    const value = useMemo(() => ({ client, state }), [client, state]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context)
        throw new Error("useAuth debe usarse dentro de <AuthProvider>.");
    const { client, state } = context;
    return {
        ...state,
        signIn: useCallback((options) => client.signIn(options), [client]),
        signOut: useCallback(() => client.signOut(), [client]),
        switchOrg: useCallback((orgId) => client.switchOrg(orgId), [client]),
        getAccessToken: useCallback(() => client.getFreshAccessToken(), [client]),
    };
}
/**
 * `fetch` con el token puesto y refrescado. Se devuelve como hook para que el módulo no tenga que
 * acordarse de pedir el token antes de cada llamada, que es donde aparecen los 401 intermitentes.
 */
export function useAuthorizedFetch() {
    const { getAccessToken } = useAuth();
    return useCallback(async (input, init) => {
        const token = await getAccessToken();
        const headers = new Headers(init?.headers);
        if (token)
            headers.set("authorization", `Bearer ${token}`);
        return fetch(input, { ...init, headers });
    }, [getAccessToken]);
}
/**
 * Selector de organización. Cambia sin volver a autenticar (`POST /oauth/switch-org`), que es lo
 * que el núcleo ya sabía hacer desde la fase 0 y nadie usaba desde una interfaz.
 */
export function OrgSwitcher({ organizations, onSwitched, }) {
    const { orgId, switchOrg, status } = useAuth();
    const [cambiando, setCambiando] = useState(false);
    if (status !== "authenticated" || organizations.length < 2)
        return null;
    return (_jsxs("label", { style: { display: "inline-flex", alignItems: "center", gap: 8, color: "var(--cnt-fg, #111827)" }, children: [_jsx("span", { style: { fontSize: 13, color: "var(--cnt-fg-muted, #6b7280)" }, children: "Organizaci\u00F3n" }), _jsx("select", { value: orgId ?? "", disabled: cambiando, onChange: (event) => {
                    const destino = event.target.value;
                    if (destino === "" || destino === orgId)
                        return;
                    setCambiando(true);
                    void switchOrg(destino)
                        .then((next) => onSwitched?.(next))
                        .finally(() => setCambiando(false));
                }, style: {
                    padding: "6px 8px",
                    borderRadius: 6,
                    border: "1px solid var(--cnt-border, #d1d5db)",
                    background: "var(--cnt-bg, #ffffff)",
                    color: "inherit",
                }, children: organizations.map((org) => (_jsx("option", { value: org.id, children: org.displayName }, org.id))) })] }));
}
/**
 * Enlaces a otros módulos con la organización ya elegida. Es el «un clic» del criterio de salida:
 * cada enlace lleva `cnt_org`, el módulo de destino lo pasa como `org_hint`, y con la sesión de
 * dominio viva no se ve ninguna pantalla de acceso.
 */
export function ModuleLauncher({ modules, orgSlug }) {
    return (_jsx("nav", { style: { display: "flex", flexWrap: "wrap", gap: 8 }, children: modules.map((module) => {
            const disponible = module.available !== false;
            const estilo = {
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid var(--cnt-border, #d1d5db)",
                color: disponible ? "var(--cnt-fg, #111827)" : "var(--cnt-fg-muted, #6b7280)",
                background: "var(--cnt-bg, #ffffff)",
                textDecoration: "none",
                opacity: disponible ? 1 : 0.6,
            };
            if (!disponible) {
                return (_jsx("span", { style: estilo, title: module.disabledReason ?? "No disponible", "aria-disabled": "true", children: module.name }, module.code));
            }
            return (_jsx("a", { href: withOrgParam(module.baseUrl, orgSlug), style: estilo, children: module.name }, module.code));
        }) }));
}
//# sourceMappingURL=index.js.map